import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Briefcase, Clapperboard, ClipboardList } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthDialog } from '../../contexts/AuthDialogContext';

const EASE = [0.22, 1, 0.36, 1];

const SIGNALS = [
  { icon: Clapperboard, label: 'Industry-led learning' },
  { icon: ClipboardList, label: 'Practical assignments' },
  { icon: Briefcase, label: 'Career-focused' },
];

const Hero = () => {
  const { user } = useAuth();
  const { openLogin } = useAuthDialog();
  const reduceMotion = useReducedMotion();

  const rise = (delay) => ({
    initial: { opacity: 0, y: reduceMotion ? 0 : 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.85, delay, ease: EASE },
  });

  return (
    <section
      data-testid="hero-section"
      className="relative isolate flex min-h-[92vh] items-center overflow-hidden bg-black"
    >
      <div className="hero-photo pointer-events-none absolute inset-y-0 right-0 w-full overflow-hidden lg:w-[57%]">
        <motion.img
          src="/main.jpeg"
          alt="A cinematographer lining up a shot on an ARRI camera"
          className={`h-full w-full object-cover object-[62%_center] ${reduceMotion ? '' : 'drift-slow'}`}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/15 to-black/45" />
        <div className="absolute inset-0 bg-black/65 lg:bg-black/15" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-32 md:px-12 md:pb-24 md:pt-40">
        <div className="max-w-xl lg:max-w-[38rem]">
          <motion.h1
            {...rise(0.05)}
            className="display-heading text-4xl text-white sm:text-6xl lg:text-[4.25rem]"
          >
            You love films.
            <br />
            <em>But do you know how to make one?</em>
          </motion.h1>

          <motion.p
            {...rise(0.18)}
            className="mt-7 max-w-md text-base leading-relaxed text-zinc-300 md:text-lg"
          >
            Learn filmmaking through structured, practical programs built by working
            filmmakers - starting with Direction.
          </motion.p>

          <motion.div {...rise(0.3)} className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/#waitlist"
              data-testid="hero-join-waitlist"
              className="btn-cine-primary rounded-md"
            >
              Join Waitlist
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-understand-cynopsis"
              className="btn-cine-outline rounded-md"
            >
              Understand Cynopsis
            </Link>
          </motion.div>

          {!user && (
            <motion.p {...rise(0.4)} className="mt-5 text-sm text-zinc-500">
              Already enrolled?{' '}
              <button
                type="button"
                onClick={openLogin}
                data-testid="hero-login-button"
                className="text-zinc-300 underline underline-offset-4 transition-colors hover:text-[#C8102E]"
              >
                Log in to your dashboard
              </button>
            </motion.p>
          )}

          <motion.ul
            {...rise(0.5)}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-white/10 pt-7"
          >
            {SIGNALS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-xs text-zinc-400">
                <span className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-white/[0.03]">
                  <Icon className="h-4 w-4 text-[#C8102E]" strokeWidth={1.6} />
                </span>
                {label}
              </li>
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
};

export default Hero;
